import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {EntityTypeEnum} from '../../shared/enums/entity-type-enum';
import {Observable, of} from 'rxjs';
import {UserEntity} from '../../shared/models/user-entity';
import {ApiUrlConstants} from '../../shared/utils/api-constants';
import {map} from 'rxjs/operators';
import {UserCreate, UserEdit, UserEditPreferences, UserEditSelf, UserView, CreateExternalUser} from '../../shared/models/user';
import {ListHttpParameters} from '../../shared/models/list-http-parameters';
import {EconomicOperatorsService} from './economic-operators.service';
import {FacilitiesService} from './facilities.service';
import {MachinesService} from './machines.service';
import {ListView} from '../../shared/models/list-view';
import {UrlConstants} from '../../shared/utils/url-constants';
import {StatusUpdateRequest} from '../../shared/models/status-update-request';
import {EntityStatusEnum} from '../../shared/enums/entity-status.enum';
import {AppConfigService} from './app-config.service';
import {SelfPasswordChange} from '../../shared/models/self-password-change';
import {AppHttpParams} from '../../shared/models/app-http-params';
import {PermissionDetails} from 'src/app/shared/models/permission-details';
import {UserListParameters} from '../../shared/models/user-list-parameters';
import {UserDeleteRequest, UserDeleteResponse} from '../../shared/models/user-delete';
import {UserKpiCounts} from '../../shared/models/standard-user';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  constructor(private http: HttpClient,
              private economicOperatorsService: EconomicOperatorsService,
              private facilitiesService: FacilitiesService,
              private machinesService: MachinesService
  ) {
  }

  create(user: UserCreate): Observable<number> {
    const appParams = new AppHttpParams({responseLabel: 'users.newUser', notifySuccess: true, notifyError: true});
    return this.http.post<number>(AppConfigService.apiUrl + ApiUrlConstants.USERS, user, {params: appParams});
  }

  update(userId: string, user: UserEdit): Observable<string> {
    const appParams = new AppHttpParams({responseLabel: 'users.editUser', notifySuccess: true});
    return this.http.patch<string>(AppConfigService.apiUrl + ApiUrlConstants.USERS + UrlConstants.SEPARATOR + userId, user, {params: appParams});
  }

  getOne(id: string): Observable<UserView> {
    const appParams = new AppHttpParams({responseLabel: 'users.viewUser', notifyError: true});
    return this.http.get<UserView>(AppConfigService.apiUrl + ApiUrlConstants.USERS + UrlConstants.SEPARATOR + id, {params: appParams});
  }

  getListView(params: ListHttpParameters): Observable<ListView<UserView>> {
    if (params.objectName && !params.objectId) {
      return of(null);
    }

    const appParams = new AppHttpParams({params: params.getHttpParams(), notifyError: true});
    return this.http.get<ListView<UserView>>(AppConfigService.apiUrl + ApiUrlConstants.USERS, {params: appParams});
  }

  getUserEntityList(entityType: EntityTypeEnum, keyword?): Observable<ListView<UserEntity>> {
    const params = new ListHttpParameters();
    params.activated = true;
    params.keyword = keyword;
    params.strictSearch = true;

    switch (entityType) {
      case EntityTypeEnum.EO:
        return this.economicOperatorsService.getListView(params).pipe(
          map(res => {
            const result = new ListView<UserEntity>();
            result.Total = res.Total;
            result.Permissions = res.Permissions;
            result.Object_List = res.Object_List.map(eo => new UserEntity(eo.EO_ID, eo.EO_Name1, EntityTypeEnum.EO));
            return result;
          }));
      case EntityTypeEnum.FACILITY:
        return this.facilitiesService.getListView(params).pipe(
          map(res => {
            const result = new ListView<UserEntity>();
            result.Total = res.Total;
            result.Permissions = res.Permissions;
            result.Object_List = res.Object_List.map(f => new UserEntity(f.F_ID, f.F_Name, EntityTypeEnum.FACILITY));
            return result;
          }));
      case EntityTypeEnum.MACHINE:
        return this.machinesService.getListView(params).pipe(
          map(res => {
            const result = new ListView<UserEntity>();
            result.Total = res.Total;
            result.Permissions = res.Permissions;
            result.Object_List = res.Object_List.map(m => new UserEntity(m.M_ID, m.M_Name || m.M_ID, EntityTypeEnum.MACHINE));
            return result;
          }));
      default:
        return of(null);
    }
  }


  getSelf(): Observable<UserView> {
    const appParams = new AppHttpParams({notifyError: true});
    return this.http.get<UserView>(AppConfigService.apiUrl + ApiUrlConstants.USERS_SELF_GET, {params: appParams});
  }

  updateSelf(user: UserEditSelf | UserEditPreferences, notifySuccess: boolean): Observable<string> {
    const appParams = new AppHttpParams({responseLabel: 'users.editSelf', notifySuccess});
    return this.http.patch<string>(AppConfigService.apiUrl + ApiUrlConstants.USERS_SELF, user, {params: appParams});
  }

  changePassword(selfPasswordChange: SelfPasswordChange) {
    const appParams = new AppHttpParams({responseLabel: 'users.changePassword', notifySuccess: true});
    return this.http.put<string>(AppConfigService.apiUrl + ApiUrlConstants.USERS_SELF_CHANGE_PASSWORD, selfPasswordChange, {params: appParams});
  }

  updateStatus(userId: string, statusUpdateRequest: StatusUpdateRequest): Observable<string> {
    const appParams = new AppHttpParams({responseLabel: 'users.updateStatus', notifyError: true, notifySuccess: true});
    return this.http.patch<string>(AppConfigService.apiUrl + ApiUrlConstants.USERS + UrlConstants.SEPARATOR + userId + ApiUrlConstants.USERS_STATUS, statusUpdateRequest, {params: appParams});
  }

  updateBulkStatus(ids: string[], entityStatus: EntityStatusEnum, comment?: string): Observable<any> {
    const appParams = new AppHttpParams({responseLabel: 'users.updateStatus', notifyError: false, notifySuccess: false});
    return this.http.patch<any>(AppConfigService.apiUrl + ApiUrlConstants.USERS + ApiUrlConstants.USERS_STATUS, {
      Ids: ids,
      EntityStatus: entityStatus,
      Comment: comment
    }, {params: appParams});
  }

  checkUserExists(email: string): Observable<PermissionDetails> {
    const appParams = new AppHttpParams({responseLabel: 'users.checkUser'});
    return this.http.get<PermissionDetails>(AppConfigService.apiUrl + ApiUrlConstants.USERNAME_EXISTS + UrlConstants.SEPARATOR + email , {params: appParams});
  }

  acceptGdpr(): Observable<string> {
    const appParams = new AppHttpParams({notifyError: true});
    return this.http.patch<string>(AppConfigService.apiUrl + ApiUrlConstants.USERS_SELF_APPROVE_GDPR, null, {params: appParams});
  }

  createExternal(user: CreateExternalUser): Observable<Object> {
    const appParams = new AppHttpParams({responseLabel: 'users.newUser', notifySuccess: true, notifyError: true});
    return this.http.post(AppConfigService.apiUrl + ApiUrlConstants.USERS_EXTERNAL, user, {params: appParams});
  }

  deleteUser(email: string): Observable<void> {
    const appParams = new AppHttpParams({responseLabel: 'users.deleteUser', notifySuccess: true, notifyError: true});
    return this.http.delete<void>(AppConfigService.apiUrl + ApiUrlConstants.USERS + UrlConstants.SEPARATOR + encodeURIComponent(email), {params: appParams});
  }

  getUserKpis(): Observable<UserKpiCounts> {
    const appParams = new AppHttpParams({notifyError: false});
    return this.http.get<UserKpiCounts>(AppConfigService.apiUrl + ApiUrlConstants.USERS_KPI, {params: appParams});
  }

  getUsers(params?: UserListParameters): Observable<ListView<UserView>> {
    const rawParams: { [key: string]: string } = {};
    if (params) {
      if (params.page !== undefined && params.page !== null) {
        rawParams['page'] = params.page.toString();
      }
      if (params.size !== undefined && params.size !== null) {
        rawParams['size'] = params.size.toString();
      }
      if (params.sort) {
        rawParams['sort'] = params.sort;
      }
      if (params.q) {
        rawParams['q'] = params.q;
      }
      if (params.status) {
        rawParams['status'] = params.status;
      }
      if (params.eo) {
        rawParams['eo'] = params.eo;
      }
      if (params.login) {
        rawParams['login'] = params.login;
      }
      if (params.from) {
        rawParams['from'] = params.from;
      }
      if (params.to) {
        rawParams['to'] = params.to;
      }
      if (params.eoName) {
        rawParams['eoName'] = params.eoName;
      }
      if (params.age !== undefined && params.age !== null && params.age !== '') {
        rawParams['age'] = params.age.toString();
      }
    }
    const appParams = new AppHttpParams({params: rawParams, notifyError: false});
    return this.http.get<ListView<UserView>>(AppConfigService.apiUrl + ApiUrlConstants.USERS, {params: appParams});
  }

  deleteUsers(request: UserDeleteRequest): Observable<UserDeleteResponse> {
    const appParams = new AppHttpParams({responseLabel: 'users.deleteUser', notifySuccess: true, notifyError: true});
    return this.http.post<UserDeleteResponse>(AppConfigService.apiUrl + ApiUrlConstants.USERS_DELETE, request, {params: appParams});
  }

}
